import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const BDMPerformanceChart = ({ data = [], isLoading = false, onRepClick, period = '7d' }) => {
  const navigate = useNavigate();
  const [selectedPeriod, setSelectedPeriod] = useState(period);

  const periodOptions = [
    { label: '7D', value: '7d' },
    { label: '30D', value: '30d' },
    { label: '90D', value: '90d' }
  ];

  const colors = [
    '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
    '#06B6D4', '#84CC16', '#F97316', '#EC4899', '#6366F1'
  ];

  // Filter data based on selected period (currently returns original data)
  const getFilteredData = () => {
    return data;
  };

  const filteredData = getFilteredData();

  const handleBDMClick = (data, index) => {
    if (data && data.payload) {
      const repData = data.payload;
      // Try to get rep initials or name
      const repIdentifier = repData.name || repData.fullName || repData.rep;
      if (repIdentifier) {
        const encodedRep = encodeURIComponent(repIdentifier);
        // Navigate to BDM report for BDM-specific data, or Advisor report for sales advisor data
        // For now, we'll use BDM report - can be adjusted based on data type
        navigate(`/reports/bdm?rep=${encodedRep}`);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-80 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!filteredData || filteredData.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Top Sales Reps</h3>
          <div className="flex space-x-2">
            {periodOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setSelectedPeriod(option.value)}
                className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                  selectedPeriod === option.value
                    ? 'bg-indigo-100 text-indigo-700 font-medium'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
        <div className="h-80 flex items-center justify-center">
          <p className="text-gray-500">No sales rep data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Top Sales Reps</h3>
        <div className="flex space-x-2">
          {periodOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => setSelectedPeriod(option.value)}
              className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                selectedPeriod === option.value
                  ? 'bg-indigo-100 text-indigo-700 font-medium'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={filteredData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="name" 
              stroke="#6b7280" 
              fontSize={11} 
              angle={-45} 
              textAnchor="end" 
              height={80}
            />
            <YAxis 
              tickFormatter={(value) => `${value}`} 
              stroke="#6b7280" 
              fontSize={12}
            />
            <Tooltip
              formatter={(value, name) => [`${value} visits`, 'Visits']}
              labelFormatter={(label, payload) => {
                const data = payload?.[0]?.payload;
                return data?.fullName || label;
              }}
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}
            />
            <Bar 
              dataKey="visits" 
              radius={[4, 4, 0, 0]}
              onClick={handleBDMClick}
              style={{ cursor: 'pointer' }}
            >
              {filteredData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={colors[index % colors.length]}
                  style={{ cursor: 'pointer' }}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* View Report Link */}
      <div className="mt-4 text-center">
        <button
          onClick={() => navigate('/reports/bdm')}
          className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-800 font-medium transition-colors cursor-pointer"
        >
          View Report
          <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
      <p className="mt-2 text-xs text-gray-500 text-center">Click on a bar to filter by rep</p>
    </div>
  );
};

export default BDMPerformanceChart;